import React, { useEffect, useState, useCallback } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../api/client';
import { MODULES } from '../../data/modules';
import { downloadCsv } from '../../utils/csv';

interface WishlistRow {
  id: number;
  email: string;
  fullName: string | null;
  phone: string | null;
  interestedModules: string[];
  createdAt: string;
}

const moduleName = (slug: string) => MODULES.find((m) => m.slug === slug)?.shortName ?? slug;

export default function AdminWishlist() {
  const [items, setItems] = useState<WishlistRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [loading, setLoading] = useState(true);

  const load = useCallback((p = 1) => {
    setLoading(true);
    api
      .get('/admin/wishlist', { params: { page: p, per_page: perPage } })
      .then((res) => {
        setItems(res.data.items);
        setTotal(res.data.total);
        setPage(p);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const exportCsv = () => {
    downloadCsv(
      `bankably-wishlist-page${page}.csv`,
      items.map((w) => ({
        name: w.fullName,
        email: w.email,
        phone: w.phone,
        interestedModules: w.interestedModules.map(moduleName).join('; '),
        joined: w.createdAt,
      }))
    );
  };

  const moduleBreakdown = (() => {
    const counts: Record<string, number> = {};
    items.forEach((w) => w.interestedModules.forEach((slug) => { counts[slug] = (counts[slug] ?? 0) + 1; }));
    const max = Math.max(1, ...Object.values(counts));
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([slug, count]) => ({ slug, count, pct: (count / max) * 100 }));
  })();

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-textPrimary">Wishlist ({total})</h1>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-surface1 border border-border text-textSecondary hover:border-gold/30 hover:text-textPrimary transition"
        >
          <Download size={14} /> Export Page
        </button>
      </div>

      {moduleBreakdown.length > 0 && (
        <div className="bg-surface1 rounded-xl border border-border p-6 mb-6">
          <h3 className="text-textPrimary font-semibold mb-4">Most-Requested Modules <span className="text-textTertiary text-xs font-normal">(this page)</span></h3>
          <div className="space-y-2.5">
            {moduleBreakdown.map((m) => (
              <div key={m.slug} className="flex items-center gap-3">
                <span className="text-textSecondary text-sm w-36 truncate flex-shrink-0">{moduleName(m.slug)}</span>
                <div className="flex-1 h-2 bg-surface2 rounded-full overflow-hidden">
                  <div className="h-full bg-gold-gradient rounded-full" style={{ width: `${m.pct}%` }} />
                </div>
                <span className="text-textTertiary text-xs w-6 text-right">{m.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Phone</th>
              <th className="text-left py-3 px-4">Interested Modules</th>
              <th className="text-left py-3 px-4">Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-textTertiary">Loading...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-textTertiary">No wishlist signups yet.</td>
              </tr>
            ) : (
              items.map((w) => (
                <tr key={w.id} className="border-b border-border/60 hover:bg-surface2/50 transition">
                  <td className="py-3 px-4 text-textPrimary">{w.fullName || '—'}</td>
                  <td className="py-3 px-4 text-textSecondary">{w.email}</td>
                  <td className="py-3 px-4 text-textTertiary">{w.phone || '—'}</td>
                  <td className="py-3 px-4 text-textTertiary">
                    {w.interestedModules.length === 0 ? '—' : w.interestedModules.map(moduleName).join(', ')}
                  </td>
                  <td className="py-3 px-4 text-textTertiary">{new Date(w.createdAt).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-textSecondary">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => load(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface1 border border-border disabled:opacity-40 hover:border-gold/30 transition"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => load(page + 1)}
              disabled={page >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface1 border border-border disabled:opacity-40 hover:border-gold/30 transition"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
